import React from "react";
import { Row, Col, Select, Button, Flex, Badge, ConfigProvider, Input, Tooltip, Space, Typography } from "antd";
import { DatePicker } from "components/Utils/momentPickers";
import "./styles.scss";
import {
  DesktopOutlined,
  MobileOutlined,
  GlobalOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  UserOutlined,
  FileSyncOutlined,
  ClearOutlined,
  FormOutlined,
} from "@ant-design/icons";
import datePickerLocale from "antd/lib/locale/pt_BR";
import { getActionText } from "../../pages/Activities/All/constants";
import { useNavigate } from "react-router-dom";
import { useUsers } from "../../hooks/useUsers";
import { useForms } from "../../hooks/useForms";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const ACTION_OPTIONS = ["CREATE", "UPDATE", "DELETE"];

const PAGE_LABELS = {
  Records: "Registro",
  Import: "Importação",
  Trash: "Lixeira",
};

const DEVICE_OPTIONS = [
  { value: "web", label: "Computador", icon: <DesktopOutlined /> },
  { value: "app", label: "Aplicativo", icon: <MobileOutlined /> },
  { value: "api", label: "API", icon: <GlobalOutlined /> },
];

const NETWORK_OPTIONS = [
  { value: "online", label: "Online", status: "success" },
  { value: "offline", label: "Offline", status: "error" },
];

/** Rótulo consistente acima de cada campo de filtro. */
function Field({ label, children, span = { xs: 24, sm: 12, md: 8, xl: 6 } }) {
  return (
    <Col {...span} style={{ marginBottom: 16 }}>
      <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
        {label}
      </Text>
      {children}
    </Col>
  );
}

export default function ActivitiesFilters({
  activeKeys,
  setActiveKeys,
  filters // This contains all the returns from useActivitiesFilters
}) {
  const navigate = useNavigate();
  const { users, loading: loadingUsers } = useUsers();
  const { forms, loading: loadingForms } = useForms();
  const {
    selectedDay, setSelectedDay,
    selectedType, setSelectedType,
    selectedPage, setSelectedPage,
    selectedForm, setSelectedForm,
    selectedAuthor, setSelectedAuthor,
    selectedDevice, setSelectedDevice,
    selectedNetwork, setSelectedNetwork,
    selectedIdsAffected, setSelectedIdsAffected,
    handleClear,
    hasAnyFilter,
    activeFilterCount,
  } = filters;

  const isOpen = activeKeys.includes("1");

  const togglePanel = () => {
    setActiveKeys(isOpen ? activeKeys.filter((k) => k !== "1") : [...activeKeys, "1"]);
  };

  const searchByLabel = (input, option) =>
    (option?.searchlabel?.toLowerCase() ?? "").includes(input.toLowerCase());

  const filterCountTooltip =
    activeFilterCount === 0
      ? null
      : activeFilterCount === 1
        ? "1 filtro aplicado"
        : `${activeFilterCount} filtros aplicados`;

  return (
    <div className="activities-filters" style={{ width: "100%" }}>
      <Flex align="center" gap={12} wrap>
        <Tooltip title={filterCountTooltip}>
          <Badge count={isOpen ? 0 : activeFilterCount} size="small" offset={[-6, 2]}>
            <Button type="primary" onClick={togglePanel}>
              Filtros {isOpen ? <CaretDownOutlined /> : <CaretRightOutlined />}
            </Button>
          </Badge>
        </Tooltip>

        <ConfigProvider locale={datePickerLocale}>
          <RangePicker
            value={selectedDay}
            onChange={(dates) => setSelectedDay(dates)}
            style={{ width: "100%", maxWidth: 360 }}
            allowClear={false}
          />
        </ConfigProvider>

        <Space style={{ marginLeft: "auto" }}>
          <Button icon={<FormOutlined />} onClick={() => navigate("/atividades/formularios/todas")}>
            Formulários
          </Button>
          <Button type="text" danger icon={<ClearOutlined />} disabled={!hasAnyFilter} onClick={handleClear}>
            Limpar filtros
          </Button>
        </Space>
      </Flex>

      {isOpen && (
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Field label="Formulário" span={{ xs: 24, sm: 12, md: 8, xl: 6 }}>
              <Select
                placeholder="Todos os formulários"
                value={selectedForm}
                onChange={setSelectedForm}
                style={{ width: "100%" }}
                allowClear
                loading={loadingForms}
                showSearch
                filterOption={searchByLabel}
              >
                {forms.map((form) => (
                  <Option key={form.id} value={form.shortName} searchlabel={form.shortName}>
                    <FileSyncOutlined style={{ marginRight: 8 }} />
                    {form.shortName}
                  </Option>
                ))}
              </Select>
            </Field>

            <Field label="Ação" span={{ xs: 24, sm: 12, md: 8, xl: 5 }}>
              <Select
                placeholder="Todas as ações"
                value={selectedType}
                onChange={setSelectedType}
                style={{ width: "100%" }}
                allowClear
              >
                {ACTION_OPTIONS.map((type) => (
                  <Option key={type} value={type}>
                    {getActionText(type)}
                  </Option>
                ))}
              </Select>
            </Field>

            <Field label="Origem" span={{ xs: 24, sm: 12, md: 8, xl: 5 }}>
              <Select
                placeholder="Todas as origens"
                value={selectedPage}
                onChange={setSelectedPage}
                style={{ width: "100%" }}
                allowClear
              >
                {Object.entries(PAGE_LABELS).map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Field>

            <Field label="Dispositivo" span={{ xs: 24, sm: 12, md: 8, xl: 8 }}>
              <Select
                mode="multiple"
                maxTagCount="responsive"
                placeholder="Todos os dispositivos"
                value={selectedDevice}
                onChange={(values) => {
                  if (values && values.length > 0) setSelectedDevice(values);
                }}
                style={{ width: "100%" }}
              >
                {DEVICE_OPTIONS.map(({ value, label, icon }) => (
                  <Option key={value} value={value}>
                    {React.cloneElement(icon, { style: { marginRight: 8 } })}
                    {label}
                  </Option>
                ))}
              </Select>
            </Field>

            <Field label="Conexão" span={{ xs: 24, sm: 12, md: 8, xl: 5 }}>
              <Select
                placeholder="Todas as conexões"
                value={selectedNetwork}
                onChange={setSelectedNetwork}
                style={{ width: "100%" }}
                allowClear
              >
                {NETWORK_OPTIONS.map(({ value, label, status }) => (
                  <Option key={value} value={value}>
                    <Badge status={status} text={label} />
                  </Option>
                ))}
              </Select>
            </Field>

            <Field label="Autor" span={{ xs: 24, sm: 12, md: 8, xl: 6 }}>
              <Select
                placeholder="Todos os autores"
                value={selectedAuthor}
                onChange={setSelectedAuthor}
                style={{ width: "100%" }}
                allowClear
                loading={loadingUsers}
                showSearch
                filterOption={searchByLabel}
              >
                <Option key="System" value="System" searchlabel="Sistema">
                  <UserOutlined style={{ marginRight: 8 }} />
                  Sistema
                </Option>
                {users.map((user) => {
                  const label = user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.email || user.uuid;

                  return (
                    <Option key={user.uuid} value={user.uuid} searchlabel={label}>
                      <UserOutlined style={{ marginRight: 8 }} />
                      {label}
                    </Option>
                  );
                })}
              </Select>
            </Field>

            <Field label="ID do dado" span={{ xs: 24, sm: 24, md: 12, xl: 9 }}>
              <Input
                placeholder="Buscar por ID"
                value={selectedIdsAffected}
                onChange={(e) => setSelectedIdsAffected(e.target.value)}
                style={{ width: "100%" }}
                allowClear
              />
            </Field>
          </Row>
      )}
    </div>
  );
}
